import { defineStore } from 'pinia';

import { computed, readonly, ref } from 'vue';
import { useWatchViewportSize } from '../composables/useHostViewport';
import type { Botconfig } from '@/types';

export const useLayoutStore = defineStore('layout', () => {
  // watch the parent viewport that determins the size of the widget
  // in case of a  normal website this is the browser window
  // in case of when we are embedeed into an iframe we watch the iframe size
  // if not isForceFullscreen is set to true
  const { viewport: hostViewport } = useWatchViewportSize();

  // ##############################
  // # Fullscreen
  // ##############################
  const isForcedFullscreen = ref<boolean>(false);
  const isFullscreen = computed(
    () => isForcedFullscreen.value || hostViewport.value.width <= 501
  );

  function setForceFullscreen(value: boolean) {
    isForcedFullscreen.value = value;
  }

  // ##############################
  // # isMobile
  // ##############################
  const isMobile = computed(() => {
    if (isForcedFullscreen.value) return true;
    return hostViewport.value.width <= 501;
  });
  const isMobileTeaser = computed(() => {
    if (isForcedFullscreen.value) return true;
    return hostViewport.value.width <= 768;
  });

  // ##############################
  // # Offsets
  // ##############################

  type Offset = { x: string; y: string };

  const teaserOffset = ref<Offset>()
  const widgetOffset = ref<Offset>()
  const buttonOffset = ref<Offset>()

  // ##############################
  // # ZIndex
  // ##############################

  const zIndex = ref(0);
  const getZIndex = computed(() => zIndex.value);
  function setZIndex(value: number) {
    zIndex.value = value;
  }

  // ##############################
  // # Alignment Origin
  // ##############################
  const alignmentOrigin = ref({
    x: 'right',
    y: 'bottom',
  });

  const ALIGNMENT_ORIGN = new RegExp('(bottom|top)\\s(left|right)', 'im');
  function setAlignmentOrigin(origin = 'bottom right') {
    // we expect something like: bottom right
    const result = ALIGNMENT_ORIGN.exec(origin);
    if (!result) {
      console.error('alignment string does not match the needed format');
      return;
    }
    // loose the orignial input string
    const [, yAxis, xAxis] = result;
    alignmentOrigin.value.x = xAxis;
    alignmentOrigin.value.y = yAxis;
  }

  // ##############################
  // # Position
  // ##############################

  function setPosition(y: string, x: string) {
    // we can do this here because y and x are always strings
    if (!x || !y) return;
    buttonOffset.value = { x, y };
    widgetOffset.value = { x, y };
    teaserOffset.value = { x, y };
  }

  function initializeFromConfig(config: Botconfig) {
    setAlignmentOrigin(
      config?.components?.widget?.global?.alignmentOrigin
    );
    setPosition(
      config?.components?.widget?.global?.position.bottom,
      config?.components?.widget?.global?.position.right
    );
  }

  return {
    isMobile: readonly(isMobile),
    isMobileTeaser: readonly(isMobileTeaser),
    alignmentOrigin: readonly(alignmentOrigin),
    buttonOffset: readonly(buttonOffset),
    widgetOffset: readonly(widgetOffset),
    teaserOffset: readonly(teaserOffset),
    zIndex: readonly(zIndex),
    isFullscreen: readonly(isFullscreen),

    hostViewPortWidth: hostViewport.value.width,
    hostViewPortHeight: hostViewport.value.height,

    initializeFromConfig,
    setAlignmentOrigin,
    getZIndex,
    setPosition,
    setZIndex,
    setForceFullscreen,
  };
});
